import { useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const LABELS = {
  rows_in_file: "Rows in file", to_import: "To import", added: "Added", updated: "Updated",
  unchanged: "Unchanged", deactivated: "Deactivated", division_headers: "Division headers",
  duplicate_rows_in_file: "Dupes in file", skipped_stale: "Skipped (stale)",
  needs_attention: "Needs attention", duplicates: "Duplicates",
};

function Stat({ k, v }) {
  const label = LABELS[k] || k.replace(/^flag_/, "flag: ").replace(/_/g, " ");
  const warn = k === "deactivated" || k === "needs_attention" || k.startsWith("flag_");
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`stat-${k}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${warn && v > 0 ? "text-amber-600" : "text-slate-800"}`}>
        {v?.toLocaleString?.() ?? v}
      </div>
    </div>
  );
}

function RowTable({ title, rows, columns, testid, total }) {
  if (!rows?.length) return null;
  const truncated = total && total > rows.length;
  return (
    <div className="mt-4" data-testid={testid}>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}{" "}
        <span className="text-slate-400">
          ({truncated ? `showing first ${rows.length} of ${total}` : rows.length})
        </span>
      </h4>
      <div className="mt-1 max-h-52 overflow-auto rounded-md border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>{columns.map((c) => (
              <th key={c.key} className="px-2 py-1.5 text-left font-medium text-slate-500">{c.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {columns.map((c) => (
                  <td key={c.key} className="px-2 py-1 text-slate-700 truncate max-w-[260px]">
                    {String(r[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Preview → explicit confirm → commit. Nothing is written until the user confirms twice.
 */
export default function ImportPreviewDialog({
  open, onOpenChange, title, preview, onCommit, children, extraTables = [],
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const counts = preview?.counts || {};
  const destructive = (counts.deactivated || 0) > 0;

  const commit = async () => {
    setBusy(true);
    setError("");
    try {
      await onCommit();
      setConfirming(false);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Import failed");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Preview only — nothing has been written yet. {preview?.file_name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-4 gap-2">
            {Object.entries(counts).map(([k, v]) => <Stat key={k} k={k} v={v} />)}
          </div>

          {children}

          {extraTables.map((t) => <RowTable key={t.testid} {...t} />)}

          {error && (
            <p className="mt-3 text-xs text-red-600 flex items-center gap-1" data-testid="import-error">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-cancel-btn">
              Cancel
            </Button>
            <Button onClick={() => setConfirming(true)} disabled={busy} data-testid="import-commit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Commit import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="import-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Commit this import?</AlertDialogTitle>
            <AlertDialogDescription>
              {counts.added || 0} added, {counts.updated || 0} updated
              {destructive ? `, ${counts.deactivated} marked inactive (never deleted)` : ""}.
              This writes to the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={commit} data-testid="import-confirm-ok">Yes, import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
